import React, { useState } from 'react';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useSearchParams } from 'react-router-dom';
import { HiOutlineLightBulb, HiOutlineSparkles, HiOutlineClock, HiOutlineCheckCircle } from 'react-icons/hi';
import { FaRobot, FaUser, FaSpinner } from 'react-icons/fa';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SentimentAnalysisCard from '../components/SentimentAnalysisCard';
import analystApi from '../analystApi';

const PromptAnalysisPage = () => {
  const [searchParams] = useSearchParams();
  /** Set by TrendVector mobile WebView (`tv_embed=1`): hide site chrome for in-app viewing. */
  const isMobileEmbed = searchParams.get('tv_embed') === '1';
  const [prompt, setPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [analysisMetadata, setAnalysisMetadata] = useState(null);
  const [error, setError] = useState(null);

  const handleAnalyze = async () => {
    if (!prompt.trim()) {
      setError('Please enter a prompt to analyze.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setAnalysisResult(null);
    setAnalysisMetadata(null);

    // --- ADD CRITICAL DEBUGGING LOGS ---
    const requestBody = { prompt: prompt.trim() };
    
    // Check for any proxy or base URL configurations
    // console.log('🔍 === ENVIRONMENT DEBUG ===');
    // console.log('📍 Current URL:', window.location.href);
    // console.log('🌐 Origin:', window.location.origin);
    // console.log('📁 Pathname:', window.location.pathname);
    // console.log('🔗 Analyst API URL:', process.env.REACT_APP_ANALYST_API_URL || 'http://localhost:8001');
    // console.log('📦 User Agent:', navigator.userAgent);
    // console.log('🌐 === END ENVIRONMENT DEBUG ===');
    
    // console.log('🔍 === FRONTEND ANALYST API REQUEST DEBUG ===');
    // console.log('📅 Timestamp:', new Date().toISOString());
    // console.log('🌐 Request Method: POST');
    // console.log('🔗 Request URL: /api/v1/analyst/prompt');
    // console.log('📝 Request Body:', JSON.stringify(requestBody, null, 2));
    // console.log('🔑 Auth Token Present:', !!localStorage.getItem('token'));
    // console.log('🔑 Auth Token Length:', localStorage.getItem('token')?.length || 0);
    // console.log('🔑 Auth Token Preview:', localStorage.getItem('token') ? `${localStorage.getItem('token').substring(0, 20)}...` : 'None');
    // console.log('🔍 === END REQUEST DEBUG ===');

    try {
      // console.log('🚀 Making hybrid analyst API request (3-minute timeout)...');
      const response = await analystApi.post('/api/v1/analyst/prompt', requestBody);
      
      // console.log('📡 Response received:');
      // console.log('   Status:', response.status);
      // console.log('   Status Text:', response.statusText);
      // console.log('   Headers:', response.headers);
      // console.log('   URL:', response.config.url);
      // console.log('   Base URL:', response.config.baseURL);

      // console.log('✅ Response OK, parsing data...');
      const data = response.data;
      // console.log('📊 Parsed Response Data:', data);
      
      // Store both the original prompt and AI response
      setAnalysisResult({ 
        originalPrompt: prompt.trim(), 
        aiResponse: data.analysis 
      });
      setAnalysisMetadata({
        symbolsAnalyzed: data.symbols_analyzed || [],
        timestamp: data.analysis_timestamp || new Date().toISOString(),
        sentimentAnalysis: data.sentiment_analysis || null
      });
      
      // console.log('✅ === ANALYST API REQUEST SUCCESSFUL ===');
    } catch (err) {
      console.error('💥 === REQUEST FAILED ===');
      console.error('Error Type:', err.constructor.name);
      console.error('Error Message:', err.message);
      console.error('Error Stack:', err.stack);
      
      if (err.message.includes('403')) {
        console.error('🔒 403 Forbidden - Access denied');
        setError('Access denied. This feature requires an EXPERT subscription plan.');
      } else if (err.message.includes('401')) {
        console.error('🔐 401 Unauthorized - Authentication required');
        setError('Please log in to access this feature.');
      } else if (err.message.includes('405')) {
        console.error('🚫 405 Method Not Allowed - This is the issue!');
        setError('Server error: Method not allowed. Please contact support.');
      } else {
        console.error('❓ Unknown error type');
        setError(err.message || 'An error occurred while analyzing your prompt.');
      }
    } finally {
      setIsLoading(false);
      // console.log('🏁 Request completed, loading set to false');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAnalyze();
    }
  };

  const examplePrompts = [
    "Should I invest in RELIANCE and TCS? I'm looking for long-term opportunities with both technical and fundamental analysis.",
    "What do you think about HDFC and ICICI for short-term trading? Include SWOT analysis.",
    "Analyze DMART and ADANIENT for swing trading opportunities with comprehensive insights.",
    "I'm interested in TATAMOTORS and MARUTI. How do technical and fundamental factors align?",
    "Should I buy INFOSYS and WIPRO for my portfolio? Provide hybrid analysis."
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {!isMobileEmbed && <Navbar />}

      {/* Main Content */}
      <div className="flex-1">
        {!isMobileEmbed && (
          <div className="bg-white shadow-sm border-b">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                    <FaRobot className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">AI Stock Analyst</h1>
                  <p className="text-gray-600">Get comprehensive analysis combining technical, fundamental & sentiment insights</p>
                </div>
              </div>
            </div>
          </div>
        )}

      <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${isMobileEmbed ? 'py-3' : 'py-8'}`}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content Area */}
          <div className="lg:col-span-2">
            {/* Features Overview */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-sm border p-6 mb-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <HiOutlineLightBulb className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Natural Language</h3>
                    <p className="text-sm text-gray-600">Ask questions naturally</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                    <HiOutlineSparkles className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Hybrid Analysis</h3>
                    <p className="text-sm text-gray-600">Technical + Fundamental</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                    <HiOutlineClock className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Real-time</h3>
                    <p className="text-sm text-gray-600">Instant insights</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-pink-100 rounded-lg flex items-center justify-center">
                    <HiOutlineSparkles className="w-5 h-5 text-pink-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Sentiment</h3>
                    <p className="text-sm text-gray-600">Market mood analysis</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Input Area */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-sm border p-6 mb-6"
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                  <FaUser className="w-4 h-4 text-gray-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Ask Your Question</h2>
              </div>
              
              <div className="space-y-4">
                <div>
                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="e.g., Should I invest in BSE and DMART? I'm looking for long-term opportunities."
                    className="w-full h-32 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    disabled={isLoading}
                  />
                </div>
                
                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-500">
                    Press Enter to analyze (Shift+Enter for new line)
                  </p>
                  <button
                    onClick={handleAnalyze}
                    disabled={isLoading || !prompt.trim()}
                    className="px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {isLoading ? (
                      <>
                        <FaSpinner className="w-4 h-4 animate-spin" />
                        <span>Analyzing...</span>
                      </>
                    ) : (
                      <>
                        <HiOutlineSparkles className="w-4 h-4" />
                        <span>Analyze</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>

            {/* Dynamic Results Section - Only appears when there's activity */}
            {(isLoading || analysisResult || error) && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl shadow-sm border p-6"
              >
                {/* Loading State */}
                {isLoading && (
                  <div className="text-center">
                    <FaSpinner className="animate-spin text-4xl text-indigo-500 mx-auto mb-4" />
                    <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-1">
                      Performing Hybrid Analysis...
                    </h2>
                    <p className="text-gray-600 mb-6">This comprehensive analysis combines technical and fundamental data for superior insights.</p>
                    <div className="max-w-md mx-auto text-left text-gray-700">
                      <ul className="space-y-2">
                        <li className="flex items-center"><span className="w-2 h-2 rounded-full bg-sky-500 mr-3"></span>Extracting symbols from your prompt...</li>
                        <li className="flex items-center"><span className="w-2 h-2 rounded-full bg-emerald-500 mr-3"></span>Fetching real-time technical data...</li>
                        <li className="flex items-center"><span className="w-2 h-2 rounded-full bg-violet-500 mr-3"></span>Querying for fundamental & SWOT analysis...</li>
                        <li className="flex items-center"><span className="w-2 h-2 rounded-full bg-pink-500 mr-3"></span>Analyzing market sentiment...</li>
                        <li className="flex items-center"><span className="w-2 h-2 rounded-full bg-orange-500 mr-3"></span>Synthesizing final report with AI...</li>
                      </ul>
                    </div>
                  </div>
                )}

                {/* Results State */}
                {!isLoading && analysisResult && !error && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                          <FaRobot className="w-4 h-4 text-blue-600" />
                        </div>
                        <h2 className="text-lg font-semibold text-gray-900">Analysis Results</h2>
                      </div>
                      {analysisMetadata && (
                        <div className="text-sm text-gray-500">
                          {analysisMetadata.symbolsAnalyzed.length > 0 && (
                            <span className="mr-4">
                              📊 {analysisMetadata.symbolsAnalyzed.length} symbol(s) analyzed
                            </span>
                          )}
                          {analysisMetadata.timestamp && (
                            <span>
                              🕒 {new Date(analysisMetadata.timestamp).toLocaleString()}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Display the original question */}
                    <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                      <h3 className="font-medium text-gray-900 mb-2">Your Question:</h3>
                      <p className="text-gray-700">{analysisResult.originalPrompt}</p>
                    </div>
                    
                    {analysisMetadata && analysisMetadata.symbolsAnalyzed.length > 0 && (
                      <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                        <p className="text-sm text-blue-800">
                          <strong>Symbols analyzed:</strong> {analysisMetadata.symbolsAnalyzed.join(', ')}
                        </p>
                      </div>
                    )}
                    
                                         <div className="prose prose-sm max-w-none">
                       <ReactMarkdown 
                         remarkPlugins={[remarkGfm]}
                         components={{
                           table: ({node, ...props}) => (
                             <div className="overflow-x-auto my-4">
                               <table className="w-full border-collapse bg-white rounded-lg shadow-sm border border-gray-200" {...props} />
                             </div>
                           ),
                           thead: ({node, ...props}) => (
                             <thead className="bg-gray-50 border-b border-gray-200" {...props} />
                           ),
                           th: ({node, ...props}) => (
                             <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-r border-gray-200 last:border-r-0" {...props} />
                           ),
                           td: ({node, ...props}) => (
                             <td className="px-4 py-3 text-sm text-gray-700 border-r border-gray-200 last:border-r-0 border-b border-gray-100" {...props} />
                           ),
                           tr: ({node, ...props}) => (
                             <tr className="hover:bg-gray-50 transition-colors duration-150" {...props} />
                           ),
                                                      h1: ({node, children, ...props}) => (
                              <h1 className="text-2xl font-bold text-gray-900 mb-4 mt-6" {...props}>{children}</h1>
                            ),
                            h2: ({node, children, ...props}) => (
                              <h2 className="text-xl font-semibold text-gray-900 mb-3 mt-5" {...props}>{children}</h2>
                            ),
                            h3: ({node, children, ...props}) => {
                              const text = children?.toString() || '';
                              const isAnalysisSection = text.includes('Technical Outlook') || 
                                                       text.includes('Fundamental Outlook') || 
                                                       text.includes('Market Sentiment') || 
                                                       text.includes('Final Verdict & Reasoning');
                              
                              if (isAnalysisSection) {
                                return (
                                  <h3 className="text-xl font-bold text-gray-900 mb-4 mt-6 p-3 bg-gray-100 rounded-lg border-l-4 border-blue-500" {...props}>
                                    {children}
                                  </h3>
                                );
                              }
                              
                              return (
                                <h3 className="text-lg font-semibold text-gray-900 mb-2 mt-4" {...props}>{children}</h3>
                              );
                            },
                            h4: ({node, children, ...props}) => (
                              <h4 className="text-base font-semibold text-gray-900 mb-2 mt-3" {...props}>{children}</h4>
                            ),
                                                      ul: ({node, children, ...props}) => (
                              <ul className="list-disc list-inside space-y-1 mb-4" {...props}>{children}</ul>
                            ),
                            ol: ({node, children, ...props}) => (
                              <ol className="list-decimal list-inside space-y-1 mb-4" {...props}>{children}</ol>
                            ),
                            li: ({node, children, ...props}) => (
                              <li className="text-gray-700" {...props}>{children}</li>
                            ),
                                                      strong: ({node, children, ...props}) => (
                              <strong className="font-semibold text-gray-900" {...props}>{children}</strong>
                            ),
                            em: ({node, children, ...props}) => (
                              <em className="italic text-gray-700" {...props}>{children}</em>
                            ),
                            blockquote: ({node, children, ...props}) => (
                              <blockquote className="border-l-4 border-blue-500 pl-4 py-2 bg-blue-50 rounded-r-lg my-4" {...props}>{children}</blockquote>
                            )
                         }}
                       >
                         {analysisResult.aiResponse.replace(/\n## Disclaimer\n\nThis analysis is generated by an AI.*$/s, '')}
                       </ReactMarkdown>
                     </div>

                                         {/* Sentiment Analysis Card */}
                     {analysisMetadata && analysisMetadata.sentimentAnalysis && (
                       <div className="mt-6">
                         <SentimentAnalysisCard 
                           sentimentData={analysisMetadata.sentimentAnalysis}
                           className="mb-6"
                         />
                       </div>
                     )}

                     {/* Disclaimer */}
                     <div className="mt-6 p-4 bg-gray-50 rounded-lg border-l-4 border-blue-500">
                       <h4 className="text-sm font-semibold text-gray-900 mb-2">Disclaimer</h4>
                       <p className="text-sm text-gray-700">
                         This analysis is generated by an AI based on proprietary technical models, public fundamental data, and market sentiment analysis and is for informational purposes only. It is not financial advice. Always conduct your own research.
                       </p>
                     </div>
                  </div>
                )}

                {/* Error State */}
                {!isLoading && error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                    <div className="flex items-center space-x-3">
                      <div className="w-6 h-6 bg-red-100 rounded-lg flex items-center justify-center">
                        <HiOutlineCheckCircle className="w-4 h-4 text-red-600" />
                      </div>
                      <p className="text-red-800">{error}</p>
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Example Prompts */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-xl shadow-sm border p-6 mb-6"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Example Prompts</h3>
              <div className="space-y-3">
                {examplePrompts.map((example, index) => (
                  <button
                    key={index}
                    onClick={() => setPrompt(example)}
                    className="w-full text-left p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors duration-200 text-sm text-gray-700"
                  >
                    {example}
                  </button>
                ))}
              </div>
            </motion.div>

            {/* How It Works */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl shadow-sm border p-6 mb-6"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4">How It Works</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-semibold text-blue-600">1</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Extract Symbols</h4>
                    <p className="text-sm text-gray-600">AI identifies stock symbols from your prompt</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-semibold text-green-600">2</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Hybrid Analysis</h4>
                    <p className="text-sm text-gray-600">Technical + Fundamental data gathered in parallel</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-pink-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-semibold text-pink-600">3</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Sentiment Analysis</h4>
                    <p className="text-sm text-gray-600">Market sentiment from news, social media & Reddit</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-semibold text-purple-600">4</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">AI Synthesis</h4>
                    <p className="text-sm text-gray-600">Comprehensive hybrid report with alignment analysis</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Tips */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4">💡 Tips</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li>• Be specific about your investment goals</li>
                <li>• Mention multiple stocks for comparison</li>
                <li>• Include your time horizon (short/long-term)</li>
                <li>• Ask about specific trading strategies</li>
                <li>• Get technical, fundamental & sentiment analysis</li>
                <li>• Understand how signals align or diverge</li>
                <li>• Consider market sentiment for timing</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </div>
      </div>

      {!isMobileEmbed && <Footer />}
    </div>
  );
};

export default PromptAnalysisPage;
